import { Box, Link, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Button from "components/basic/button";
import Typography from "components/basic/typography";

interface DocumentCardProps {
  title: string;
  /** Read-only label/value lines shown while not editing. */
  rows: { label: string; value: React.ReactNode }[];
  documentUrl?: string | null;
  editing: boolean;
  onEdit: () => void;
  children: React.ReactNode;
}

/** A document's current details, swapped for its edit form on "Update". */
const DocumentCard: React.FC<DocumentCardProps> = ({
  title,
  rows,
  documentUrl,
  editing,
  onEdit,
  children,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("profile");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 2,
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5" style={{ fontWeight: "bold" }}>
          {title}
        </Typography>
        {!editing && (
          <Button size="small" variant="outlined" onClick={onEdit}>
            {t("documents.update")}
          </Button>
        )}
      </Box>

      {editing
        ? children
        : rows.map(({ label, value }) => (
            <Box key={label} display="flex" justifyContent="space-between">
              <Typography variant="body2" style={{ opacity: 0.7 }}>
                {label}
              </Typography>
              <Typography variant="body2" style={{ fontWeight: "bold" }}>
                {value}
              </Typography>
            </Box>
          ))}

      {!editing && documentUrl && (
        <Link href={documentUrl} target="_blank" rel="noopener noreferrer">
          {t("documents.view")}
        </Link>
      )}
    </Box>
  );
};

export default DocumentCard;
